import React, { Suspense } from 'react';
import { createFrontendPlugin, PageBlueprint, createRouteRef } from '@backstage/frontend-plugin-api';
import { createPermission } from '@backstage/plugin-permission-common';
import { RequirePermission } from '@backstage/plugin-permission-react';
import DescriptionIcon from '@material-ui/icons/Description';

export interface StaticPageConfig {
  title?: string;
  path?: string;
  icon?: React.ReactElement;
  submenu?: string;
}

export interface StaticContentPluginOptions {
  pages?: Record<string, StaticPageConfig>;
}

function toTitleCase(slug: string): string {
  return slug
    .split('-')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

const submenuRegistry = new Map<string, string>();

export function getStaticContentSubmenuRegistry(): ReadonlyMap<string, string> {
  return submenuRegistry;
}

const STATIC_CONTENT_PERMISSION_PREFIX = 'static-content.access.';

const submenuPermissionCache = new Map<string, ReturnType<typeof createPermission>>();

// Keyed on the raw submenu label — ElitePermissionPolicy (backend) matches
// permission names against this same prefix + label, so keep both in sync.
export function getStaticContentSubmenuPermission(submenuLabel: string) {
  let permission = submenuPermissionCache.get(submenuLabel);
  if (!permission) {
    permission = createPermission({
      name: `${STATIC_CONTENT_PERMISSION_PREFIX}${submenuLabel}`,
      attributes: { action: 'read' },
    });
    submenuPermissionCache.set(submenuLabel, permission);
  }
  return permission;
}

function resolveComponent(mod: any): React.ComponentType {
  return (
    mod.default ??
    Object.values(mod).find(
      (v): v is React.ComponentType => typeof v === 'function',
    )
  );
}

// Bundled eagerly — every user downloads this code regardless of permission
// checks. Use for pages with no access restriction.
const staticContext = (require as any).context(
  '../../components/html/static',
  false,
  /\.tsx$/,
);

// Code-split per file and only fetched after the permission check passes.
// Use for pages behind a group-gated submenu. Because loading is lazy,
// title/icon/submenu can't be read from these files at build time — they
// must come from the `pages` override map instead (see README in that dir).
const gatedContext = (require as any).context(
  '../../components/html/static-gated',
  false,
  /\.tsx$/,
  'lazy',
);

const lazyComponentCache = new Map<string, React.LazyExoticComponent<React.ComponentType>>();

function getLazyGatedComponent(key: string): React.LazyExoticComponent<React.ComponentType> {
  let LazyComponent = lazyComponentCache.get(key);
  if (!LazyComponent) {
    LazyComponent = React.lazy(async () => ({
      default: resolveComponent(await gatedContext(key)),
    }));
    lazyComponentCache.set(key, LazyComponent);
  }
  return LazyComponent;
}

export function createStaticContentPlugin(options: StaticContentPluginOptions = {}) {
  const eagerPageConfigs = (staticContext.keys() as string[]).map(key => {
    const slug = key.replace(/^\.\//, '').replace(/\.tsx$/, '');
    const mod = staticContext(key);
    const overrides = options.pages?.[slug] ?? {};
    return {
      slug,
      key,
      gated: false as const,
      routeRef: createRouteRef(),
      overrides,
      derivedTitle: mod.pageTitle ?? toTitleCase(slug),
      derivedIcon: mod.pageIcon ?? <DescriptionIcon />,
      derivedSubmenu: overrides.submenu ?? mod.pageSubmenu,
    };
  });

  const gatedPageConfigs = (gatedContext.keys() as string[]).map(key => {
    const slug = key.replace(/^\.\//, '').replace(/\.tsx$/, '');
    const overrides = options.pages?.[slug] ?? {};
    if (!overrides.submenu) {
      throw new Error(
        `static-content: gated page "${slug}" must declare a submenu via ` +
          `the "pages" override map in App.tsx — in-file metadata can't be ` +
          `read for lazily-loaded pages.`,
      );
    }
    return {
      slug,
      key,
      gated: true as const,
      routeRef: createRouteRef(),
      overrides,
      derivedTitle: overrides.title ?? toTitleCase(slug),
      derivedIcon: overrides.icon ?? <DescriptionIcon />,
      derivedSubmenu: overrides.submenu as string,
    };
  });

  const pageConfigs = [...eagerPageConfigs, ...gatedPageConfigs];

  submenuRegistry.clear();
  for (const { slug, derivedSubmenu } of pageConfigs) {
    if (derivedSubmenu) {
      submenuRegistry.set(`page:static-content/sc-${slug}`, derivedSubmenu);
    }
  }

  const routes = Object.fromEntries(
    pageConfigs.map(({ slug, routeRef }) => [slug, routeRef]),
  );

  const extensions = pageConfigs.map(
    ({ slug, key, gated, routeRef, overrides, derivedTitle, derivedIcon, derivedSubmenu }) =>
      PageBlueprint.make({
        name: `sc-${slug}`,
        params: {
          path: overrides.path ?? `/${slug}`,
          title: overrides.title ?? derivedTitle,
          icon: overrides.icon ?? derivedIcon,
          routeRef,
          loader: async () => {
            let content: React.ReactElement;
            if (gated) {
              const LazyComponent = getLazyGatedComponent(key);
              content = (
                <Suspense fallback={null}>
                  <LazyComponent />
                </Suspense>
              );
            } else {
              const mod = staticContext(key);
              const Component = resolveComponent(mod);
              content = <Component />;
            }

            if (!derivedSubmenu) {
              return content;
            }

            return (
              <RequirePermission
                permission={getStaticContentSubmenuPermission(derivedSubmenu)}
              >
                {content}
              </RequirePermission>
            );
          },
        },
      }),
  );

  return createFrontendPlugin({
    pluginId: 'static-content',
    routes,
    extensions,
  });
}
